// frontend/src/modules/esign/pages/DocumentDetailPage.tsx
//
// Tenant-facing detail view for a single e-sign document: recipient rows
// (with resend), events timeline, void, and download. A manual Refresh
// button covers the 'sent' polling need (no auto-poll — YAGNI per brief).
import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { RefreshCw, Download, Ban, Copy, BookmarkPlus, CalendarClock, PenLine, SlidersHorizontal, AlertTriangle } from 'lucide-react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { NoAccessState } from '../../../design-system/components/NoAccessState';
import { ErrorState } from '../../../design-system/components/ErrorState';
import { LoadingState } from '../../../design-system/components/LoadingState';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { statusPill, type StatusTone } from '../../../design-system/visual';
import { formatDate, formatDateTime } from '../../../design-system/format';
import { usePermission } from '../../../core/auth/usePermission';
import { useConfirm, usePrompt } from '../../../components/ui/confirm-dialog';
import { esignApi, esignTemplatesApi } from '../api';
import type { DocumentDetail, EsignDocumentStatus, EsignRecipient, EsignRecipientStatus } from '../types';

const STATUS_TONE: Record<EsignDocumentStatus, StatusTone> = {
  draft: 'draft',
  sent: 'info',
  completed: 'success',
  declined: 'destructive',
  voided: 'muted',
};

const STATUS_LABEL: Record<EsignDocumentStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  completed: 'Completed',
  declined: 'Declined',
  voided: 'Voided',
};

const RECIPIENT_STATUS_TONE: Record<EsignRecipientStatus, StatusTone> = {
  pending: 'muted',
  viewed: 'info',
  signed: 'success',
  declined: 'destructive',
};

const RECIPIENT_STATUS_LABEL: Record<EsignRecipientStatus, string> = {
  pending: 'Pending',
  viewed: 'Viewed',
  signed: 'Signed',
  declined: 'Declined',
};

const EVENT_LABEL: Record<string, string> = {
  created: 'Document created',
  sent: 'Sent for signature',
  viewed: 'Viewed',
  signed: 'Signed',
  declined: 'Declined',
  voided: 'Voided',
  completed: 'Completed — fully executed',
  resent: 'Resent',
  seal_failed: 'Seal failed',
};

export function DocumentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const canView = usePermission('esign.view');
  const canSend = usePermission('esign.send');
  // Void is gated server-side by esign.send only (POST /:id/void). A
  // manage-only user would 403 at the route, so don't show a dead-action
  // button for them — the service still separately enforces creator-or-manage
  // for non-creators who DO hold esign.send.
  const canVoid = canSend;
  const confirm = useConfirm();
  const prompt = usePrompt();

  const [detail, setDetail] = React.useState<DocumentDetail | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [resendingId, setResendingId] = React.useState<string | null>(null);
  const [voiding, setVoiding] = React.useState(false);
  const [downloading, setDownloading] = React.useState(false);
  const [savingTemplate, setSavingTemplate] = React.useState(false);
  const [extending, setExtending] = React.useState(false);
  const [mySignEligible, setMySignEligible] = React.useState(false);
  const [signLinkError, setSignLinkError] = React.useState(false);
  const [signingNow, setSigningNow] = React.useState(false);

  const load = React.useCallback(() => {
    if (!id || !canView) return;
    setLoading(true);
    setError(null);
    esignApi.getDocument(id)
      .then((d) => setDetail(d))
      .catch((err: any) => setError(err?.message ?? 'Failed to load this document'))
      .finally(() => setLoading(false));
  }, [id, canView]);

  React.useEffect(() => { load(); }, [load]);

  // Lazy "Sign now" self-serve check — only meaningful while the document is
  // in-flight. A 404 `not_a_pending_signer` is the expected/common outcome
  // (most viewers of a document aren't also one of its signers) — swallow it
  // silently. Any OTHER failure (401/500/network) is NOT the same as "not a
  // signer" — surface a retry affordance instead of silently hiding the
  // Sign now button from a genuine signer.
  const checkMySignLink = React.useCallback(() => {
    if (!id || detail?.document.status !== 'sent') return () => {};
    let cancelled = false;
    setSignLinkError(false);
    esignApi.checkMySignEligibility(id)
      .then(() => { if (!cancelled) { setMySignEligible(true); setSignLinkError(false); } })
      .catch((err: any) => {
        if (cancelled) return;
        if (err?.status === 404) {
          setMySignEligible(false);
        } else {
          console.warn('[esign] my-sign-link probe failed', err);
          setMySignEligible(false);
          setSignLinkError(true);
        }
      });
    return () => { cancelled = true; };
  }, [id, detail?.document.status]);

  React.useEffect(() => checkMySignLink(), [checkMySignLink]);

  async function handleSignNow() {
    if (!id) return;
    setSigningNow(true);
    try {
      const { url } = await esignApi.mySignLink(id);
      window.location.assign(url);
    } catch (err: any) {
      toast.error('Could not open your sign link', { description: err?.message ?? String(err) });
      setSigningNow(false);
    }
  }

  if (!canView) {
    return (
      <div className="min-h-screen">
        <PageHeader eyebrow="Money · E-Sign" title="Document" />
        <div className="px-6 sm:px-8 lg:px-10 pb-12">
          <NoAccessState description="Your role doesn't include e-sign. Ask a workspace owner if you think you should have access." />
        </div>
      </div>
    );
  }

  if (loading && !detail) {
    return (
      <div className="min-h-screen">
        <PageHeader eyebrow="Money · E-Sign" title="Document" />
        <div className="px-6 sm:px-8 lg:px-10 pb-12">
          <LoadingState />
        </div>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="min-h-screen">
        <PageHeader eyebrow="Money · E-Sign" title="Document" />
        <div className="px-6 sm:px-8 lg:px-10 pb-12">
          <ErrorState title="Couldn't load this document" description={error ?? undefined} onRetry={load} />
        </div>
      </div>
    );
  }

  const { document: doc, recipients, events } = detail;

  async function handleResend(recipientId: string) {
    if (!id) return;
    setResendingId(recipientId);
    try {
      await esignApi.resend(id, recipientId);
      toast.success('Reminder sent');
      load();
    } catch (err: any) {
      toast.error('Failed to resend', { description: err?.message ?? String(err) });
    } finally {
      setResendingId(null);
    }
  }

  async function handleVoid() {
    if (!id) return;
    const ok = await confirm({
      title: 'Void this document?',
      description: 'Every signer will lose access to their sign link. This cannot be undone.',
      confirmLabel: 'Void document',
      destructive: true,
    });
    if (!ok) return;
    setVoiding(true);
    try {
      await esignApi.voidDoc(id, undefined);
      toast.success('Document voided');
      load();
    } catch (err: any) {
      toast.error('Failed to void document', { description: err?.message ?? String(err) });
    } finally {
      setVoiding(false);
    }
  }

  async function handleDownload(opts: { original?: boolean } = {}) {
    if (!id) return;
    setDownloading(true);
    try {
      const url = await esignApi.downloadPdf(id, opts);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      toast.error('Download failed', { description: err?.message ?? String(err) });
    } finally {
      setDownloading(false);
    }
  }

  async function handleSaveAsTemplate() {
    if (!id) return;
    const name = await prompt({
      title: 'Save as template',
      description: 'Roles and field placements are copied. Recipient names and emails are not — you fill those in each time you use the template.',
      defaultValue: doc.title,
      placeholder: doc.title,
    });
    if (!name || !name.trim()) return;
    setSavingTemplate(true);
    try {
      const { template } = await esignTemplatesApi.fromDocument(id, name.trim());
      toast.success('Template saved');
      navigate(`/dashboard/esign/templates/${template.id}`);
    } catch (err: any) {
      toast.error('Failed to save template', { description: err?.message ?? String(err) });
    } finally {
      setSavingTemplate(false);
    }
  }

  async function handleExtend() {
    if (!id) return;
    const ok = await confirm({
      title: 'Extend signing links?',
      description: 'Extend signing links by 14 days?',
      confirmLabel: 'Extend',
    });
    if (!ok) return;
    setExtending(true);
    try {
      await esignApi.extend(id);
      toast.success('Expiry extended');
      load();
    } catch (err: any) {
      toast.error('Failed to extend', { description: err?.message ?? String(err) });
    } finally {
      setExtending(false);
    }
  }

  function handleCopyVerifyLink() {
    if (!doc.signature_number) return;
    const url = `https://worqly.io/verify/${doc.signature_number}`;
    navigator.clipboard.writeText(url).then(
      () => toast.success('Verify link copied'),
      () => toast.error('Could not copy link'),
    );
  }

  // A completed document with no signature number was never sealed: the render/
  // upload pipeline failed and `seal_error` holds why. Only call the download
  // "sealed PDF" when a sealed PDF actually exists — the server now refuses to
  // serve the unsigned original under that label, and this is the matching
  // half of that fix on the UI side.
  const sealFailed = doc.status === 'completed' && !doc.signature_number;
  const downloadLabel = doc.status === 'completed' && !sealFailed ? 'Download sealed PDF' : 'Download';
  const canVoidNow = canVoid && doc.status !== 'voided' && doc.status !== 'completed' && doc.status !== 'declined';
  const isExpired = doc.status === 'sent' && !!doc.expires_at && new Date(doc.expires_at) < new Date();

  return (
    <div className="min-h-screen">
      <PageHeader
        eyebrow="Money · E-Sign"
        title={doc.title}
        chip={
          <div className="flex items-center gap-2">
            <span className={statusPill(STATUS_TONE[doc.status])}>{STATUS_LABEL[doc.status]}</span>
            {doc.status === 'sent' && doc.expires_at && (
              isExpired ? (
                <span className={statusPill('destructive')}>Expired</span>
              ) : (
                <span className="text-[12px] text-ink/50 dark:text-white/50">Expires {formatDate(doc.expires_at)}</span>
              )
            )}
          </div>
        }
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="gap-1.5" onClick={load} disabled={loading}>
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </Button>
            {!sealFailed && (
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => handleDownload()} disabled={downloading}>
                <Download className="h-3.5 w-3.5" />
                {downloadLabel}
              </Button>
            )}
            {/* A draft is only half-built: without this the wizard is
                unreachable once you navigate away, stranding the document. */}
            {canSend && doc.status === 'draft' && (
              <Button size="sm" className="gap-1.5" onClick={() => navigate(`/dashboard/esign/${doc.id}/prepare`)}>
                <SlidersHorizontal className="h-3.5 w-3.5" />
                Continue preparing
              </Button>
            )}
            {canSend && (
              <Button variant="outline" size="sm" className="gap-1.5" onClick={handleSaveAsTemplate} disabled={savingTemplate}>
                <BookmarkPlus className="h-3.5 w-3.5" />
                {savingTemplate ? 'Saving…' : 'Save as template'}
              </Button>
            )}
            {canSend && doc.status === 'sent' && doc.expires_at && (
              <Button variant="outline" size="sm" className="gap-1.5" onClick={handleExtend} disabled={extending}>
                <CalendarClock className="h-3.5 w-3.5" />
                {extending ? 'Extending…' : 'Extend'}
              </Button>
            )}
            {mySignEligible && (
              <Button size="sm" className="gap-1.5" onClick={handleSignNow} disabled={signingNow}>
                <PenLine className="h-3.5 w-3.5" />
                {signingNow ? 'Opening…' : 'Sign now'}
              </Button>
            )}
            {signLinkError && (
              <span className="text-[12px] text-ink/45 dark:text-white/45">
                Couldn't check whether you can sign —{' '}
                <button
                  type="button"
                  className="underline underline-offset-2 hover:text-ink/70 dark:hover:text-white/70"
                  onClick={checkMySignLink}
                >
                  Retry
                </button>
              </span>
            )}
            {canVoidNow && (
              <Button variant="destructive" size="sm" className="gap-1.5" onClick={handleVoid} disabled={voiding}>
                <Ban className="h-3.5 w-3.5" />
                Void
              </Button>
            )}
          </div>
        }
      />

      <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-8">
        {sealFailed && (
          <div
            data-testid="seal-failed-banner"
            className="rounded-2xl bg-destructive/5 ring-1 ring-destructive/25 p-5 space-y-2"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />
              <span className="text-[13.5px] font-semibold text-ink dark:text-white">
                Signed, but not sealed
              </span>
            </div>
            <p className="text-[13px] text-ink/70 dark:text-white/70 leading-relaxed">
              Every signer has signed and their signatures are safely recorded, but building the
              certificate-stamped PDF failed, so there is no sealed copy and no verification number yet.
              {doc.seal_error && (
                <>
                  {' '}Reason: <span className="font-mono text-[12px]">{doc.seal_error}</span>
                </>
              )}
            </p>
            <p className="text-[13px] text-ink/70 dark:text-white/70 leading-relaxed">
              Refresh to retry sealing — it is safe to retry as often as you like and will never
              duplicate signatures or issue a second number.
            </p>
            <div className="flex items-center gap-2 pt-1 flex-wrap">
              <Button variant="outline" size="sm" className="gap-1.5" onClick={load} disabled={loading}>
                <RefreshCw className="h-3.5 w-3.5" />
                Retry sealing
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => handleDownload({ original: true })}
                disabled={downloading}
              >
                <Download className="h-3.5 w-3.5" />
                Download unsigned original
              </Button>
            </div>
          </div>
        )}

        {doc.status === 'completed' && doc.signature_number && (
          <div className="rounded-2xl bg-white/70 dark:bg-white/[0.04] ring-1 ring-ink/8 dark:ring-white/10 p-5 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Badge variant="success">Fully executed</Badge>
              <span className="font-mono text-[13px] text-ink/70 dark:text-white/70">{doc.signature_number}</span>
            </div>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={handleCopyVerifyLink}>
              <Copy className="h-3.5 w-3.5" />
              Copy verify link
            </Button>
          </div>
        )}

        <section>
          <h2 className="text-[13px] font-semibold uppercase tracking-[0.1em] text-ink/55 dark:text-white/55 mb-3">
            Recipients
          </h2>
          <div className="rounded-2xl bg-white/70 dark:bg-white/[0.04] ring-1 ring-ink/8 dark:ring-white/10 divide-y divide-ink/8 dark:divide-white/10">
            {recipients.map((r: EsignRecipient) => (
              <div key={r.id} className="flex items-center justify-between gap-4 px-5 py-4 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-ink dark:text-white">{r.name}</span>
                    {r.role === 'cc' && (
                      <Badge variant="secondary">CC — view only</Badge>
                    )}
                  </div>
                  <div className="text-[13px] text-ink/60 dark:text-white/60">{r.email}</div>
                  {r.viewed_at && (
                    <div className="text-[12px] text-ink/45 dark:text-white/45">Viewed {formatDateTime(r.viewed_at)}</div>
                  )}
                  {r.signed_at && (
                    <div className="text-[12px] text-ink/45 dark:text-white/45">Signed {formatDateTime(r.signed_at)}</div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {r.role === 'signer' && (
                    <span className={statusPill(RECIPIENT_STATUS_TONE[r.status])}>{RECIPIENT_STATUS_LABEL[r.status]}</span>
                  )}
                  {canSend && r.role === 'signer' && r.status !== 'signed' && r.status !== 'declined' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleResend(r.id)}
                      disabled={resendingId === r.id}
                    >
                      Resend
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-[13px] font-semibold uppercase tracking-[0.1em] text-ink/55 dark:text-white/55 mb-3">
            Activity
          </h2>
          <div className="rounded-2xl bg-white/70 dark:bg-white/[0.04] ring-1 ring-ink/8 dark:ring-white/10 divide-y divide-ink/8 dark:divide-white/10">
            {events.length === 0 ? (
              <div className="px-5 py-4 text-[13px] text-ink/50 dark:text-white/50">No activity yet.</div>
            ) : (
              events.map((ev) => (
                <div key={ev.id} className="flex items-center justify-between gap-4 px-5 py-3 text-[13px]">
                  <span className="text-ink/70 dark:text-white/70">{EVENT_LABEL[ev.event] ?? ev.event}</span>
                  <span className="text-ink/45 dark:text-white/45 tabular-nums">{formatDateTime(ev.created_at)}</span>
                </div>
              ))
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
